import type { Triangulation } from '../../geo/types';

export type Corners = readonly [number, number, number];

/**
 * A triangulation rewritten in place, where triangles are only ever appended or retired, never reused.
 * The triangles around a vertex are those of the input, listed once up front through `firstAround`,
 * and those added since; reading either skips the retired.
 */
export interface TriangleSheet {
  readonly positions: Float64Array;
  readonly corners: number[];
  readonly alive: boolean[];
  readonly firstAround: Uint32Array;
  readonly inputAround: Uint32Array;
  readonly addedAround: Map<number, number[]>;
}

// Counting sort of the input triangles by corner: one pass counts, one sums, one files.
export function createTriangleSheet(input: Triangulation): TriangleSheet {
  const vertices = input.positions.length / 2;
  const firstAround = new Uint32Array(vertices + 1);
  for (const v of input.indices) firstAround[v + 1] = firstAround[v + 1]! + 1;
  for (let v = 0; v < vertices; v++) firstAround[v + 1] = firstAround[v + 1]! + firstAround[v]!;
  const next = firstAround.slice(0, vertices);
  const inputAround = new Uint32Array(input.indices.length);
  input.indices.forEach((v, i) => {
    inputAround[next[v]!] = Math.floor(i / 3);
    next[v] = next[v]! + 1;
  });
  return {
    positions: input.positions,
    corners: Array.from(input.indices),
    alive: new Array<boolean>(input.indices.length / 3).fill(true),
    firstAround,
    inputAround,
    addedAround: new Map(),
  };
}

export function cornersOf(sheet: TriangleSheet, t: number): Corners {
  return [sheet.corners[t * 3]!, sheet.corners[t * 3 + 1]!, sheet.corners[t * 3 + 2]!];
}

export function aroundOf(sheet: TriangleSheet, v: number): number[] {
  const found: number[] = [];
  const end = v + 1 < sheet.firstAround.length ? sheet.firstAround[v + 1]! : 0;
  for (let i = sheet.firstAround[v] ?? 0; i < end; i++) {
    if (sheet.alive[sheet.inputAround[i]!]) found.push(sheet.inputAround[i]!);
  }
  for (const t of sheet.addedAround.get(v) ?? []) if (sheet.alive[t]) found.push(t);
  return found;
}

export function trianglesOn(sheet: TriangleSheet, a: number, b: number): number[] {
  return aroundOf(sheet, a).filter((t) => cornersOf(sheet, t).includes(b));
}

export function apexOf(sheet: TriangleSheet, t: number, a: number, b: number): number {
  return cornersOf(sheet, t).find((v) => v !== a && v !== b)!;
}

export function addTriangle(sheet: TriangleSheet, corners: Corners): number {
  const t = sheet.alive.length;
  sheet.corners.push(...corners);
  sheet.alive.push(true);
  for (const v of corners) {
    const listed = sheet.addedAround.get(v);
    if (listed) listed.push(t);
    else sheet.addedAround.set(v, [t]);
  }
  return t;
}

export function retireTriangle(sheet: TriangleSheet, t: number): void {
  sheet.alive[t] = false;
}

export function onOutline(sheet: TriangleSheet, v: number): boolean {
  return aroundOf(sheet, v).some((t) =>
    cornersOf(sheet, t).some((w) => w !== v && trianglesOn(sheet, v, w).length === 1)
  );
}

export function neighboursOf(sheet: TriangleSheet, v: number): Set<number> {
  const corners = aroundOf(sheet, v).flatMap((t) => cornersOf(sheet, t));
  return new Set(corners.filter((w) => w !== v));
}

export function liveIndices(sheet: TriangleSheet): Uint32Array {
  return Uint32Array.from(sheet.alive.flatMap((alive, t) => (alive ? cornersOf(sheet, t) : [])));
}
